import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DatePicker, Button, message, Spin, Checkbox } from 'antd';
import { getMediaPlansList } from '../../../core/api/mediaplan';

const { RangePicker } = DatePicker;


const MultiSelectFilter = React.memo(function MultiSelectFilter({
  title,
  options,
  selectedValues,
  onSelectionChange,
  filterKey,
  loading,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredOptions = options.filter((opt) =>
    (opt.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCheckboxChange = (optionId, checked, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const newValues = checked
      ? [...selectedValues, optionId]
      : selectedValues.filter((id) => id !== optionId);
    onSelectionChange(filterKey, newValues);
  };

  return (
    <div className="mb-3">
      <span className="fw-bold">{title}</span>
      <div className="mt-2">
        <div
          className="form-control d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', minHeight: '38px' }}
          onClick={() => setIsOpen((v) => !v)}
        >
          <span className="text-muted">
            {selectedValues.length > 0
              ? `${selectedValues.length} selected`
              : 'Select items'}
          </span>
          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
        </div>

        {isOpen && (
          <div
            className="border border-top-0 p-2"
            style={{ maxHeight: '200px', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />

            {loading ? (
              <div className="text-center py-2">
                <Spin size="small" />
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="text-center py-2 text-muted">No options available</div>
            ) : (
              filteredOptions.map((option) => {
                const checked = selectedValues.includes(option.id);
                return (
                  <div
                    key={option.id}
                    className="form-check"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCheckboxChange(option.id, !checked, e);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange(option.id, e.target.checked, e);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {option.name}
                    </Checkbox>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
});


const MediaPlanFilter = ({
  onFilterChange,
  userSelectedFilters = {},
  onClose = () => undefined,
}) => {
  const selectedClients = useSelector((state) => state.selectedClients);

  const [tempFilters, setTempFilters] = useState({
    dateRange: userSelectedFilters?.dateRange || null,
    productVariations: userSelectedFilters?.productVariations || [],
    strategies: userSelectedFilters?.strategies || [],
    campaignCodes: userSelectedFilters?.campaignCodes || [],
    offerCodifications: userSelectedFilters?.offerCodifications || [],
    brands: userSelectedFilters?.brands || [],
  });

  const [loading, setLoading] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [strategyOptions, setStrategyOptions] = useState([]);
  const [campaignCodeOptions, setCampaignCodeOptions] = useState([]);
  const [offerCodificationOptions, setOfferCodificationOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);

  // --- Fetch filter options (optionally filtered by brand)
  const fetchFilterOptions = async (brandFilter = []) => {
    setLoading(true);
    try {
      const response = await getMediaPlansList(
        selectedClients,
        1,
        1000,
        'list',
        null,
        null,
        brandFilter.length ? { brands: brandFilter } : {}
      );
      const mediaPlans = response?.data?.results || [];

      // --- Strategies
      const uniqueStrategies = [];
      const strategySet = new Set();
      mediaPlans.forEach((plan) => {
        const name = plan?.strategy?.strategy;
        if (name && !strategySet.has(name)) {
          strategySet.add(name);
          uniqueStrategies.push({ id: name, name });
        }
      });
      setStrategyOptions(uniqueStrategies);

      // --- Product Variations
      const uniqueProducts = [];
      const productSet = new Set();
      mediaPlans.forEach((plan) => {
        if (Array.isArray(plan?.offer_details?.product_variations_detail)) {
          plan.offer_details.product_variations_detail.forEach((p) => {
            if (p?.description && !productSet.has(p.description)) {
              productSet.add(p.description);
              uniqueProducts.push({ id: p.description, name: p.description });
            }
          });
        }
      });
      setProductOptions(uniqueProducts);

      // --- Campaign Codes
      const uniqueCampaignCodes = [
        ...new Set(mediaPlans.map((plan) => plan.campaign_code).filter(Boolean)),
      ].map((code) => ({ id: code, name: code }));
      setCampaignCodeOptions(uniqueCampaignCodes);

      // --- Offer Codes
      const uniqueOfferCodes = [];
      const offerCodeSet = new Set();
      mediaPlans.forEach((plan) => {
        const code = plan?.offer_details?.code_offer;
        if (code && !offerCodeSet.has(code)) {
          offerCodeSet.add(code);
          uniqueOfferCodes.push({ id: code, name: code });
        }
      });
      setOfferCodificationOptions(uniqueOfferCodes);

      // --- Brands (only when fetching all, not when filtered by brand)
      if (brandFilter.length === 0) {
        const uniqueBrands = [];
        const brandSet = new Set();
        mediaPlans.forEach((plan) => {
          const brandName = plan?.offer_details?.brand_name;
          const clientId = plan?.offer_details?.client?.id;
          if (brandName && clientId && !brandSet.has(clientId)) {
            brandSet.add(clientId);
            uniqueBrands.push({ id: clientId, name: brandName });
          }
        });
        setBrandOptions(uniqueBrands);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      message.error('Failed to fetch filter options');
    } finally {
      setLoading(false);
    }
  };

  // --- Fetch filters on load
  useEffect(() => {
    fetchFilterOptions();
  }, [selectedClients]);

  // --- When brand filter changes, refresh dependent filters
  useEffect(() => {
    if (tempFilters.brands?.length === 1) {
      fetchFilterOptions(tempFilters.brands);
    } else {
      fetchFilterOptions(); // fetch all if no brand or multiple selected
    }

    // Reset dependent filters when brand changes
    setTempFilters((prev) => ({
      ...prev,
      productVariations: [],
      strategies: [],
      campaignCodes: [],
      offerCodifications: [],
    }));
  }, [tempFilters.brands]);

  const handleFilterSelection = (filterType, values) => {
    setTempFilters((prev) => ({
      ...prev,
      [filterType]: values,
    }));
  };

  const handleDateRangeChange = (dateRange) => {
    setTempFilters((prev) => ({ ...prev, dateRange }));
  };

const handleApplyFilters = () => {
    const filtersForApi = {};
  
    // --- Date range
    if (tempFilters.dateRange?.length === 2) {
      filtersForApi.startDate = tempFilters.dateRange[0].format('YYYY-MM-DD');
      filtersForApi.endDate = tempFilters.dateRange[1].format('YYYY-MM-DD');
    }
  
    // --- Product Variations
    if (tempFilters.productVariations?.length) {
      filtersForApi.productVariations = tempFilters.productVariations;
    }
  
    // --- Strategy
    if (tempFilters.strategies?.length) {
      filtersForApi.strategies = tempFilters.strategies;
    }
  
    // --- Campaign Codes
    if (tempFilters.campaignCodes?.length) {
      filtersForApi.campaignCodes = tempFilters.campaignCodes;
    }
  
    // --- Offer Codifications
    if (tempFilters.offerCodifications?.length) {
      filtersForApi.offerCodifications = tempFilters.offerCodifications;
    }
  
    // --- Brands
    if (tempFilters.brands?.length) {
      filtersForApi.brands = tempFilters.brands;
    }
  
    onFilterChange(filtersForApi);
  };
  
  const handleClearFilters = () => {
    setTempFilters({
      dateRange: null,
      productVariations: [],
      strategies: [],
      campaignCodes: [],
      offerCodifications: [],
      brands: [],
    });
    onFilterChange({});
  };

  return (
    <div className="filter-panel">
      <div className="mb-3">
        <span className="fw-bold">Date Range</span>
        <div className="mt-2">
          <RangePicker
            value={tempFilters.dateRange}
            onChange={handleDateRangeChange}
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            placeholder={['Start date', 'End date']}
          />
        </div>
      </div>

      <MultiSelectFilter
        title="Brands"
        options={brandOptions}
        selectedValues={tempFilters.brands}
        onSelectionChange={handleFilterSelection}
        filterKey="brands"
        loading={loading}
      />

      <MultiSelectFilter
        title="Product Variations"
        options={productOptions}
        selectedValues={tempFilters.productVariations}
        onSelectionChange={handleFilterSelection}
        filterKey="productVariations"
        loading={loading}
      />

      <MultiSelectFilter
        title="Strategy"
        options={strategyOptions}
        selectedValues={tempFilters.strategies}
        onSelectionChange={handleFilterSelection}
        filterKey="strategies"
        loading={loading}
      />

      <MultiSelectFilter
        title="Campaign Code"
        options={campaignCodeOptions}
        selectedValues={tempFilters.campaignCodes}
        onSelectionChange={handleFilterSelection}
        filterKey="campaignCodes"
        loading={loading}
      />

      <MultiSelectFilter
        title="Offer Code"
        options={offerCodificationOptions}
        selectedValues={tempFilters.offerCodifications}
        onSelectionChange={handleFilterSelection}
        filterKey="offerCodifications"
        loading={loading}
      />

      <div className="d-flex gap-2 mt-4">
        <Button type="primary" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button onClick={handleClearFilters}>Clear Filters</Button>
      </div>
    </div>
  );


};

export default MediaPlanFilter;
